<script>
import { mapActions, mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Spinner from 'shared/components/Spinner.vue';

const MAX_INSTRUCTION_LENGTH = 4000;

export default {
  name: 'OnboardingSandboxInstructionsPanel',
  components: { Spinner },
  data() {
    return {
      draft: '',
      savedInstruction: '',
      draftModel: null,
      savedModel: null,
    };
  },
  computed: {
    ...mapGetters('sandbox', [
      'instruction',
      'isLoadingInstruction',
      'isSavingInstruction',
      'selectedModel',
      'isLoadingModel',
      'isSavingModel',
      'llmList',
      'isLoadingLlmList',
      'creditsRemaining',
    ]),
    // Same catalogue ChatBotConfiguration's LLM picker uses, marked up with
    // vue-multiselect's $isDisabled so a model the account can't currently
    // afford (credit_cost > what's left of the sandbox's credit quota) shows
    // greyed-out instead of silently failing on save. The currently-selected
    // model stays selectable even if it no longer fits, so switching away
    // from it doesn't get blocked by its own cost.
    modelOptions() {
      return this.llmList.map(option => ({
        ...option,
        $isDisabled:
          option.creditCost != null &&
          this.creditsRemaining != null &&
          option.creditCost > this.creditsRemaining &&
          option.name !== this.draftModel,
      }));
    },
    selectedModelOption: {
      get() {
        return (
          this.modelOptions.find(option => option.name === this.draftModel) ||
          null
        );
      },
      set(option) {
        this.draftModel = option ? option.name : null;
      },
    },
    charCount() {
      return this.draft.length;
    },
    isOverLimit() {
      return this.charCount > MAX_INSTRUCTION_LENGTH;
    },
    isEmpty() {
      return this.draft.trim().length === 0;
    },
    isValid() {
      return !this.isEmpty && !this.isOverLimit;
    },
    // Compared against local "last saved" snapshots rather than the store
    // getters directly — if the backend ever echoes back a value that isn't
    // byte-identical to what was sent (different trimming/normalization),
    // comparing against the getter would leave this permanently dirty after
    // the very first successful save. Snapshots are only ever updated by
    // this component, right when it knows a save actually succeeded.
    isInstructionDirty() {
      return this.draft.trim() !== this.savedInstruction.trim();
    },
    isModelDirty() {
      return this.draftModel !== this.savedModel;
    },
    isDirty() {
      return this.isInstructionDirty || this.isModelDirty;
    },
    isSaving() {
      return this.isSavingInstruction || this.isSavingModel;
    },
    canSave() {
      return this.isValid && this.isDirty && !this.isSaving;
    },
    validationMessage() {
      if (this.isOverLimit) {
        return this.$t('ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.TOO_LONG');
      }
      if (this.isEmpty) {
        return this.$t('ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.REQUIRED');
      }
      return '';
    },
  },
  async mounted() {
    await Promise.all([
      this.fetchInstruction(),
      this.fetchSandboxModel(),
      this.fetchAvailableModels(),
    ]);
    this.draft = this.instruction || '';
    this.savedInstruction = this.instruction || '';
    this.draftModel = this.selectedModel;
    this.savedModel = this.selectedModel;
  },
  methods: {
    ...mapActions('sandbox', [
      'fetchInstruction',
      'updateInstruction',
      'fetchSandboxModel',
      'updateSandboxModel',
      'fetchAvailableModels',
    ]),
    // One button persists both fields — only the ones actually changed are
    // sent, each surfaces its own error independently, but a single success
    // (either or both) is enough to notify the chat that behavior changed.
    async handleSave() {
      if (!this.canSave) return;

      const tasks = [];
      if (this.isInstructionDirty) {
        tasks.push({
          type: 'instruction',
          errorKey: 'ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.SAVE_ERROR',
          onSuccess: () => {
            this.savedInstruction = this.draft.trim();
          },
          promise: this.updateInstruction(this.draft.trim()),
        });
      }
      if (this.isModelDirty) {
        tasks.push({
          type: 'model',
          errorKey:
            'ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.MODEL_SAVE_ERROR',
          onSuccess: () => {
            this.savedModel = this.draftModel;
          },
          promise: this.updateSandboxModel(this.draftModel),
        });
      }

      const results = await Promise.allSettled(tasks.map(t => t.promise));

      let instructionChanged = false;
      let modelChanged = false;
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          tasks[index].onSuccess();
          if (tasks[index].type === 'instruction') instructionChanged = true;
          if (tasks[index].type === 'model') modelChanged = true;
          return;
        }
        useAlert(result.reason?.message || this.$t(tasks[index].errorKey));
      });

      if (instructionChanged || modelChanged) {
        this.$emit('updated', { instructionChanged, modelChanged });
      }
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col gap-2 w-full md:flex-1 md:min-w-0 md:border-r md:border-slate-100 md:dark:border-slate-800 md:pr-6"
  >
    <h3 class="text-sm font-medium text-slate-700 dark:text-slate-200">
      {{ $t('ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.TITLE') }}
    </h3>
    <p class="text-xs text-slate-400 dark:text-slate-500">
      {{ $t('ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.HINT') }}
    </p>

    <div
      v-if="isLoadingInstruction"
      class="flex items-center justify-center py-8"
    >
      <Spinner size="small" />
    </div>
    <template v-else>
      <div class="relative">
        <textarea
          v-model="draft"
          data-testid="sandbox_instructions_input"
          rows="6"
          maxlength="4000"
          class="resize-none block m-0 h-40 p-2.5 pb-8 w-full text-sm text-slate-900 bg-slate-25 rounded-lg border focus:ring-woot-500 dark:bg-slate-800 dark:placeholder-slate-500 dark:text-white"
          :class="
            !isValid
              ? 'border-red-400 focus:border-red-400 dark:border-red-500'
              : 'border-slate-100 focus:border-woot-500 dark:border-slate-700'
          "
          :placeholder="
            $t('ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.PLACEHOLDER')
          "
        />
        <span
          class="absolute bottom-3 right-3 text-xs text-slate-400 dark:text-slate-500 pointer-events-none"
        >
          {{
            $t('ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.CHAR_COUNT', {
              count: charCount,
              max: 4000,
            })
          }}
        </span>
      </div>
      <span v-if="validationMessage" class="text-xs text-red-500">
        {{ validationMessage }}
      </span>

      <div class="flex flex-col gap-1 mt-2">
        <label class="text-xs font-medium text-slate-500 dark:text-slate-400">
          {{ $t('ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.LLM_LABEL') }}
        </label>
        <div data-testid="sandbox_llm_select">
          <multiselect
            v-model="selectedModelOption"
            :options="modelOptions"
            label="displayName"
            track-by="name"
            :loading="isLoadingModel || isLoadingLlmList"
            :allow-empty="false"
            :placeholder="
              $t('ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.LLM_PLACEHOLDER')
            "
          >
            <template #option="{ option }">
              <div class="flex flex-col">
                <span>{{ option.displayName }}</span>
                <span
                  v-if="option.creditCost != null"
                  class="text-xs text-slate-500"
                >
                  {{
                    $t(
                      'ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.LLM_CREDIT_COST',
                      { cost: option.creditCost }
                    )
                  }}
                </span>
              </div>
            </template>
            <template #singleLabel="{ option }">
              <span>{{ option.displayName }}</span>
            </template>
          </multiselect>
        </div>
      </div>

      <woot-button
        data-testid="sandbox_instructions_save_button"
        class="self-end mt-2"
        :is-loading="isSaving"
        :disabled="!canSave"
        @click="handleSave"
      >
        {{ $t('ONBOARDING_SHELL.STEPS.SANDBOX.INSTRUCTIONS.SAVE') }}
      </woot-button>
    </template>
  </div>
</template>
